import React, { useState } from 'react';
import { Calendar, Car, Home, Building2, User, Mail, Phone, FileText, CheckCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const SERVICE_TYPES = [
  { value: 'automotive', label: 'Automotive', icon: Car, description: 'Car, truck, or SUV window tinting' },
  { value: 'residential', label: 'Residential', icon: Home, description: 'Home window tinting' },
  { value: 'commercial', label: 'Commercial', icon: Building2, description: 'Business or office window tinting' },
];

const BookNowPage = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    service_type: '',
    preferred_date: '',
    name: '',
    email: '',
    phone: '',
    vehicle_info: '',
    notes: '',
  });

  // Calendar state
  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const selectService = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
    setError('');
  };

  const selectDate = (dateStr) => {
    setFormData(prev => ({ ...prev, preferred_date: dateStr }));
    setError('');
  };

  // Calendar helpers
  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1); }
    else setCalMonth(calMonth - 1);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1); }
    else setCalMonth(calMonth + 1);
  };

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const renderCalendar = () => {
    const days = daysInMonth(calMonth, calYear);
    const firstDay = firstDayOfMonth(calMonth, calYear);
    const cells = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="h-10" />);
    }

    for (let d = 1; d <= days; d++) {
      const dateObj = new Date(calYear, calMonth, d);
      const dateStr = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const isSunday = dateObj.getDay() === 0;
      const isPast = dateObj < new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const isSelected = formData.preferred_date === dateStr;
      const isToday = dateObj.toDateString() === today.toDateString();
      const isDisabled = isSunday || isPast;

      cells.push(
        <button
          key={d}
          type="button"
          disabled={isDisabled}
          onClick={() => selectDate(dateStr)}
          className={`h-10 w-full rounded-lg text-sm font-medium transition-all duration-200
            ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-red-50 hover:text-red-600 cursor-pointer'}
            ${isSelected ? 'bg-red-600 text-white hover:bg-red-700 hover:text-white' : ''}
            ${isToday && !isSelected ? 'ring-2 ring-red-300' : ''}
          `}
        >
          {d}
        </button>
      );
    }

    return cells;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service_type: formData.service_type,
        preferred_date: formData.preferred_date,
      };
      if (formData.vehicle_info) payload.vehicle_info = formData.vehicle_info;
      if (formData.notes) payload.notes = formData.notes;

      await axios.post('/api/reservations', payload);
      setIsComplete(true);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Please try again or call us at (713) 446-8928.';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = formData.service_type !== '';
  const canProceedStep2 = formData.preferred_date !== '';
  const canSubmit = formData.name && formData.email && formData.phone;

  // Confirmation screen
  if (isComplete) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-lg mx-auto px-4">
          <Card className="text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Reservation Submitted!</h2>
              <p className="text-gray-600 mb-6">
                We've received your request for <strong>{SERVICE_TYPES.find(s => s.value === formData.service_type)?.label}</strong> on <strong>{new Date(formData.preferred_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                <div className="flex items-start space-x-2">
                  <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-blue-900">What happens next?</p>
                    <p className="text-sm text-blue-700 mt-1">We'll review your request and confirm a specific time slot. You'll receive an email and text once confirmed.</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Questions? Call us at <a href="tel:7134468928" className="text-red-600 font-medium">(713) 446-8928</a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Book an Appointment</h1>
          <p className="text-gray-600">Schedule your window tinting service in 3 easy steps</p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center justify-center mb-8 space-x-2">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-200 ${step >= s ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {s}
              </div>
              {s < 3 && <div className={`w-16 h-1 rounded ${step > s ? 'bg-red-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Service Type */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Select a Service</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {SERVICE_TYPES.map(svc => {
                const Icon = svc.icon;
                const isSelected = formData.service_type === svc.value;
                return (
                  <button
                    key={svc.value}
                    type="button"
                    onClick={() => selectService(svc.value)}
                    className={`w-full flex items-center space-x-4 p-4 rounded-lg border-2 transition-all duration-200 text-left
                      ${isSelected ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'}
                    `}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isSelected ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{svc.label}</p>
                      <p className="text-sm text-gray-500">{svc.description}</p>
                    </div>
                    {isSelected && <CheckCircle className="h-5 w-5 text-red-600 ml-auto" />}
                  </button>
                );
              })}
              <div className="pt-4">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!canProceedStep1}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Date */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-red-600" />
                <span>Choose a Preferred Date</span>
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">We're closed on Sundays. Grayed-out dates are unavailable.</p>
            </CardHeader>
            <CardContent>
              {/* Calendar */}
              <div className="max-w-sm mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <button type="button" onClick={prevMonth} className="p-2 rounded-lg hover:bg-gray-100">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="font-semibold text-lg">{monthNames[calMonth]} {calYear}</span>
                  <button type="button" onClick={nextMonth} className="p-2 rounded-lg hover:bg-gray-100">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                    <div key={d} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {renderCalendar()}
                </div>
              </div>

              {formData.preferred_date && (
                <div className="mt-4 text-center text-sm font-medium text-green-700 bg-green-50 rounded-lg p-2">
                  Selected: {new Date(formData.preferred_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Continue
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Contact Info */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Your Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name" className="flex items-center space-x-1 mb-1">
                  <User className="h-4 w-4 text-gray-500" /><span>Name *</span>
                </Label>
                <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="John Smith" required />
              </div>

              <div>
                <Label htmlFor="email" className="flex items-center space-x-1 mb-1">
                  <Mail className="h-4 w-4 text-gray-500" /><span>Email *</span>
                </Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" required />
              </div>

              <div>
                <Label htmlFor="phone" className="flex items-center space-x-1 mb-1">
                  <Phone className="h-4 w-4 text-gray-500" /><span>Phone *</span>
                </Label>
                <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="(713) 555-1234" required />
              </div>

              {formData.service_type === 'automotive' && (
                <div>
                  <Label htmlFor="vehicle_info" className="flex items-center space-x-1 mb-1">
                    <Car className="h-4 w-4 text-gray-500" /><span>Vehicle Info</span>
                  </Label>
                  <Input id="vehicle_info" name="vehicle_info" value={formData.vehicle_info} onChange={handleInputChange} placeholder="2024 Toyota Camry" />
                </div>
              )}

              <div>
                <Label htmlFor="notes" className="flex items-center space-x-1 mb-1">
                  <FileText className="h-4 w-4 text-gray-500" /><span>Additional Notes</span>
                </Label>
                <Textarea id="notes" name="notes" value={formData.notes} onChange={handleInputChange} placeholder="Any special requests or details..." rows={3} />
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <p className="font-semibold mb-2">Reservation Summary</p>
                <p><span className="text-gray-500">Service:</span> {SERVICE_TYPES.find(s => s.value === formData.service_type)?.label}</p>
                <p><span className="text-gray-500">Date:</span> {new Date(formData.preferred_date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <Button variant="outline" onClick={() => setStep(2)} className="flex-1">Back</Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || isSubmitting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Reservation'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BookNowPage;
